<template>
  <div class="home-beneficiary-page">
    <div class="page-layout" :class="{ 'sidebar-right': sidebarPosition === 'right' }">
      <!-- 사이드바 (CAION 스타일) -->
      <aside class="cashbook-sidebar">
        <button 
          class="sidebar-position-toggle"
          @click="toggleSidebarPosition"
          :title="sidebarPosition === 'left' ? '우측으로 이동' : '좌측으로 이동'"
        >
          <v-icon size="14">{{ sidebarPosition === 'left' ? 'mdi-arrow-right' : 'mdi-arrow-left' }}</v-icon>
        </button>

        <!-- 연월 선택 -->
        <div class="sidebar-ym-selector">
          <button class="ym-nav-btn" @click="prevMonth" :disabled="!canGoPrev">
            <v-icon size="18">mdi-chevron-left</v-icon>
          </button>
          <div class="ym-display" @click="setPeriodMode('month')">
            <span class="ym-value">{{ selectedYear }}.{{ String(selectedMonth).padStart(2, '0') }}</span>
          </div>
          <button class="ym-nav-btn" @click="nextMonth" :disabled="!canGoNext">
            <v-icon size="18">mdi-chevron-right</v-icon>
          </button>
        </div>

        <!-- 페이지 타이틀 -->
        <div class="sidebar-title-badge">
          <v-icon size="14">mdi-account-heart</v-icon>
          <span>수급자 일정</span>
        </div>

        <!-- 요약 정보 -->
        <div class="sidebar-summary">
          <div class="summary-item">
            <div class="summary-header">
              <span class="dot" style="background: #1e3a5f"></span>
              <span class="label">전체 수급자</span>
            </div>
            <span class="value">{{ allBeneficiaryList.length }}명</span>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-item">
            <div class="summary-header">
              <span class="dot" style="background: #10b981"></span>
              <span class="label">이번달</span>
            </div>
            <span class="value">{{ monthBeneficiaries.size }}명</span>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-item">
            <div class="summary-header">
              <span class="dot" style="background: #6366f1"></span>
              <span class="label">서비스 개월</span>
            </div>
            <span class="value">{{ totalServiceMonths }}개월</span>
          </div>
        </div>

        <!-- 기간 필터 -->
        <div class="sidebar-filter">
          <div class="filter-header">
            <v-icon size="14" color="#64748b">mdi-calendar-range</v-icon>
            <span>기간 선택</span>
          </div>
          <div class="filter-buttons">
            <button 
              class="filter-btn" 
              :class="{ active: periodMode === 'all' }"
              @click="setPeriodMode('all')"
            >전체</button>
            <button 
              class="filter-btn" 
              :class="{ active: periodMode === 'month' }"
              @click="setPeriodMode('month')"
            >{{ selectedMonth }}월</button>
          </div>
        </div>

        <!-- 검색 -->
        <div class="sidebar-filter">
          <div class="filter-header">
            <v-icon size="14" color="#64748b">mdi-magnify</v-icon>
            <span>수급자 검색</span>
          </div>
          <div class="search-input-wrapper">
            <input
              type="text"
              class="search-input"
              v-model="searchKeyword"
              placeholder="이름 검색..."
            />
            <button v-if="searchKeyword" class="search-clear" @click="searchKeyword = ''">
              <v-icon size="12">mdi-close</v-icon>
            </button>
          </div>
        </div>
      </aside>

      <!-- 메인 컨텐츠 -->
      <main class="page-main">
        <div v-if="isLoading" class="loading-state">
          <v-progress-circular indeterminate color="primary" size="48" />
          <span>데이터를 불러오는 중...</span>
        </div>

        <section v-else class="beneficiary-list-section">
          <div class="section-header">
            <div class="header-title">
              <div class="title-icon">
                <v-icon size="18" color="white">mdi-calendar-account</v-icon>
              </div>
              <span class="title-text">수급자 일정 목록</span>
              <span class="title-count">{{ filteredBeneficiaryList.length }}명</span>
              <span class="title-badge">일정계획 기준</span>
            </div>
            <div class="header-actions">
              <button 
                class="batch-pdf-btn"
                @click="printAllBeneficiariesPdf"
                :disabled="isPdfLoading || filteredBeneficiaryList.length === 0 || periodMode === 'all'"
                :title="periodMode === 'all' ? '특정 월을 선택해주세요' : '전체 수급자 일정표 PDF 출력'"
              >
                <v-icon size="18">{{ isPdfLoading ? 'mdi-loading mdi-spin' : 'mdi-file-pdf-box' }}</v-icon>
                <span>전체 일정표 출력</span>
              </button>
              <span class="current-month-label">{{ displayPeriodLabel }} 기준</span>
            </div>
          </div>

          <table class="beneficiary-table" v-if="filteredBeneficiaryList.length > 0">
            <thead>
              <tr>
                <th class="col-no">No</th>
                <th>수급자명</th>
                <th>인정번호</th>
                <th class="text-center">첫 서비스</th>
                <th class="text-center">최근 서비스</th>
                <th class="text-center">서비스일/시간</th>
                <th class="text-center col-action">일정보기</th>
                <th class="text-center col-action">상세보기</th>
                <th class="text-center col-action">일정표</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(beneficiary, index) in filteredBeneficiaryList" :key="beneficiary.patient_code">
                <tr 
                  :class="{ selected: selectedBeneficiary?.patient_code === beneficiary.patient_code }"
                  class="beneficiary-row"
                >
                  <td class="col-no">{{ index + 1 }}</td>
                  <td>
                    <div class="patient-name">
                      <div class="patient-icon">
                        <v-icon size="12">mdi-account</v-icon>
                      </div>
                      {{ beneficiary.patient_name }}
                    </div>
                  </td>
                  <td>{{ maskCode(beneficiary.patient_code) }}</td>
                  <td class="text-center">
                    <span class="date-text">{{ formatDateShort(beneficiary.first_service_date) }}</span>
                  </td>
                  <td class="text-center">
                    <span class="date-text">{{ formatDateShort(beneficiary.last_service_date) }}</span>
                  </td>
                  <td class="text-center">
                    <span class="service-info">
                      <template v-if="periodMode === 'month'">
                        {{ beneficiary.service_days || 0 }}일 / {{ formatMinutes(beneficiary.total_minutes) }}
                      </template>
                      <template v-else>
                        {{ beneficiary.total_months || 0 }}개월
                      </template>
                    </span>
                  </td>
                  <td class="text-center col-action">
                    <button 
                      class="action-btn calendar-btn"
                      @click="openCalendarModal(beneficiary)"
                      title="일정 캘린더 보기"
                    >
                      <v-icon size="16">mdi-calendar-clock</v-icon>
                    </button>
                  </td>
                  <td class="text-center col-action">
                    <button 
                      class="action-btn detail-btn"
                      :class="{ active: selectedBeneficiary?.patient_code === beneficiary.patient_code }"
                      @click="toggleBeneficiary(beneficiary)"
                      title="상세보기"
                    >
                      <v-icon size="16">
                        {{ selectedBeneficiary?.patient_code === beneficiary.patient_code ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                      </v-icon>
                    </button>
                  </td>
                  <td class="text-center col-action">
                    <button 
                      class="action-btn pdf-btn"
                      @click="printSchedulePdf(beneficiary)"
                      :disabled="periodMode === 'all'"
                      title="일정표 PDF 출력"
                    >
                      <v-icon size="16">mdi-file-pdf-box</v-icon>
                    </button>
                  </td>
                </tr>

                <!-- 상세 정보 확장 행 -->
                <tr 
                  v-if="selectedBeneficiary?.patient_code === beneficiary.patient_code" 
                  class="detail-row"
                >
                  <td colspan="9">
                    <div class="inline-detail">
                      <div v-if="monthlySummaryLoading" class="detail-loading">
                        <v-progress-circular indeterminate size="24" />
                      </div>
                      <div class="monthly-summary" v-else-if="monthlySummary.length > 0">
                        <div 
                          class="month-card" 
                          v-for="month in monthlySummary" 
                          :key="month.year_month"
                        >
                          <div class="month-header">
                            <span class="month-label">{{ formatYearMonth(month.year_month) }}</span>
                          </div>
                          <div class="month-stats">
                            <div class="stat-item">
                              <div class="stat-value">{{ month.service_days }}</div>
                              <div class="stat-label">서비스일</div>
                            </div>
                            <div class="stat-item">
                              <div class="stat-value">{{ formatMinutes(month.total_minutes) }}</div>
                              <div class="stat-label">총 시간</div>
                            </div>
                          </div>
                          <div class="month-caregivers" v-if="month.caregivers">
                            <v-icon size="12" class="mr-1">mdi-account-hard-hat</v-icon>
                            <span>{{ month.caregivers }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="empty-text" v-else>서비스 이력 데이터가 없습니다</div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>

          <div class="empty-state" v-else>
            <div class="empty-icon">
              <v-icon size="48">mdi-calendar-account-outline</v-icon>
            </div>
            <div class="empty-text">일정계획 데이터가 없습니다</div>
            <div class="empty-hint">
              롱텀케어에서 일정계획 데이터를 가져오세요
            </div>
            <button class="empty-request-btn" @click="showLtcsRequestModal = true">
              <v-icon size="18">mdi-cloud-download</v-icon>
              <span>일정계획 데이터 요청</span>
            </button>
          </div>
        </section>
      </main>
    </div>

    <!-- 일정계획 데이터 요청 모달 -->
    <LtcsRequestModal 
      v-model="showLtcsRequestModal" 
      :default-year-month="selectedYM"
      :member-code="memberCode"
      :biz-gubun="bizGubun"
      @requested="onLtcsRequested"
    />

    <!-- 수급자 일정 캘린더 모달 -->
    <BeneficiaryCalendarModal
      v-model="showCalendarModal"
      :beneficiary="calendarBeneficiary"
      :beneficiaries="filteredBeneficiaryList"
      :member-code="memberCode"
      :biz-gubun="bizGubun"
      :year="selectedYear"
      :month="selectedMonth"
      @change-beneficiary="onCalendarBeneficiaryChange"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, inject, type Ref } from 'vue';
import { useStore } from 'vuex';
import { homeScheduleApi } from '@/services/api';
import LtcsRequestModal from '@/components/member/schedule/LtcsRequestModal.vue';
import BeneficiaryCalendarModal from '@/components/member/schedule/BeneficiaryCalendarModal.vue';
import { useMemberYearMonthOptional } from '@/composables/useMemberYearMonth';

// MemberDetailLayout에서 provide한 값들
const injectedMemberData = inject<Ref<any>>('memberData', ref(null));
const injectedBizGubun = inject<Ref<string>>('selectedBizGubun', ref(''));

interface Beneficiary {
  patient_code: string;
  patient_name: string;
  first_service_date: string;
  last_service_date: string;
  total_months?: number;
  service_days?: number;
  total_minutes?: number;
}

interface MonthlySummary {
  year_month: string;
  service_days: number;
  caregiver_count: number;
  caregivers: string;
  total_minutes: number;
  suga_types: string;
}

const store = useStore();
const memberCode = computed(() => injectedMemberData.value?.m_code || store.state.user?.m_code);
const bizGubun = computed(() => injectedBizGubun.value || store.state.user?.m_biz_gubun);

const isLoading = ref(true);
const isPdfLoading = ref(false);
const monthlySummaryLoading = ref(false);
const sidebarPosition = ref<'left' | 'right'>('left');
const searchKeyword = ref('');
const activeTab = ref<'schedule'>('schedule');
const showLtcsRequestModal = ref(false);

const allBeneficiaryList = ref<Beneficiary[]>([]);
const monthBeneficiaries = ref<Set<string>>(new Set());
const selectedBeneficiary = ref<Beneficiary | null>(null);
const monthlySummary = ref<MonthlySummary[]>([]);
const calendarBeneficiary = ref<Beneficiary | null>(null);
const showCalendarModal = ref(false);

const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth() + 1;

const periodMode = ref<'all' | 'month'>('month');
const { selectedYear, selectedMonth, yearMonth: selectedYM } = useMemberYearMonthOptional(memberCode);

const nextMonthYear = currentMonth === 12 ? currentYear + 1 : currentYear;
const nextMonthMonth = currentMonth === 12 ? 1 : currentMonth + 1;

const canGoPrev = computed(() => {
  return !(selectedYear.value === currentYear - 5 && selectedMonth.value === 1);
});

const canGoNext = computed(() => {
  return !(selectedYear.value === nextMonthYear && selectedMonth.value === nextMonthMonth);
});

const displayPeriodLabel = computed(() => {
  if (periodMode.value === 'all') {
    return '전체 기간';
  }
  return `${selectedYear.value}년 ${selectedMonth.value}월`;
});

const filteredBeneficiaryList = computed(() => {
  let list = allBeneficiaryList.value;

  if (searchKeyword.value) {
    list = list.filter(b => b.patient_name.includes(searchKeyword.value));
  }

  if (periodMode.value === 'month') {
    list = list.filter(b => monthBeneficiaries.value.has(b.patient_code));
  }

  return list;
});

const totalServiceMonths = computed(() => {
  return allBeneficiaryList.value.reduce((sum, b) => sum + (b.total_months || 0), 0);
});

const toggleSidebarPosition = () => {
  sidebarPosition.value = sidebarPosition.value === 'left' ? 'right' : 'left';
};

const setPeriodMode = (mode: 'all' | 'month') => {
  periodMode.value = mode;
  fetchData();
};

const prevMonth = () => {
  if (!canGoPrev.value) return;
  if (selectedMonth.value === 1) {
    selectedYear.value--;
    selectedMonth.value = 12;
  } else {
    selectedMonth.value--;
  }
};

const nextMonth = () => {
  if (!canGoNext.value) return;
  if (selectedMonth.value === 12) {
    selectedYear.value++;
    selectedMonth.value = 1;
  } else {
    selectedMonth.value++;
  }
};

const formatMinutes = (minutes: number | null | undefined): string => {
  if (!minutes) return '0분';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}분`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

const formatYearMonth = (ym: string): string => {
  if (!ym || ym.length !== 6) return ym;
  return `${ym.substring(0, 4)}년 ${parseInt(ym.substring(4))}월`;
};

const formatDateShort = (dateStr: string): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
};

const maskCode = (code: string): string => {
  if (!code || code.length < 3) return code || '-';
  return code.slice(0, -3) + '***';
};

const fetchAllBeneficiaries = async () => {
  if (!memberCode.value) return;
  try {
    const res = await homeScheduleApi.getBeneficiaries(memberCode.value, bizGubun.value);
    if (res.data.success) {
      allBeneficiaryList.value = res.data.data || [];
    }
  } catch (error) {
    console.error('전체 수급자 목록 조회 오류:', error);
  }
};

const fetchMonthBeneficiaries = async () => {
  if (!memberCode.value) return;
  try {
    const res = await homeScheduleApi.getBeneficiariesByMonth(memberCode.value, selectedYM.value, bizGubun.value);
    if (res.data.success) {
      const codes = new Set<string>();
      const monthData = res.data.data || [];
      monthData.forEach((b: Beneficiary) => codes.add(b.patient_code));
      monthBeneficiaries.value = codes;
      
      allBeneficiaryList.value = allBeneficiaryList.value.map(ben => {
        const monthInfo = monthData.find((m: Beneficiary) => m.patient_code === ben.patient_code);
        if (monthInfo) {
          return { ...ben, service_days: monthInfo.service_days, total_minutes: monthInfo.total_minutes };
        }
        return ben;
      });
    }
  } catch (error) {
    console.error('월별 수급자 목록 조회 오류:', error);
  }
};

const fetchData = async () => {
  isLoading.value = true;
  try {
    await fetchAllBeneficiaries();
    if (periodMode.value === 'month') {
      await fetchMonthBeneficiaries();
    } else {
      monthBeneficiaries.value = new Set(allBeneficiaryList.value.map(b => b.patient_code));
    }
  } finally {
    isLoading.value = false;
  }
};

const toggleBeneficiary = async (beneficiary: Beneficiary) => {
  if (selectedBeneficiary.value?.patient_code === beneficiary.patient_code) {
    selectedBeneficiary.value = null;
    return;
  }
  selectedBeneficiary.value = beneficiary;
  await fetchMonthlySummary(beneficiary.patient_code);
};

const fetchMonthlySummary = async (patientCode: string) => {
  if (!memberCode.value) return;
  monthlySummaryLoading.value = true;
  try {
    const res = await homeScheduleApi.getBeneficiaryMonthlySummary(memberCode.value, patientCode, bizGubun.value);
    if (res.data.success) {
      monthlySummary.value = res.data.data || [];
    }
  } catch (error) {
    console.error('월별 요약 조회 오류:', error);
    monthlySummary.value = [];
  } finally {
    monthlySummaryLoading.value = false;
  }
};

const printSchedulePdf = async (beneficiary: Beneficiary) => {
  if (!memberCode.value || periodMode.value === 'all') return;
  try {
    const res = await homeScheduleApi.downloadBeneficiaryPdf(memberCode.value, beneficiary.patient_code, selectedYM.value, bizGubun.value);
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `일정표_${beneficiary.patient_name}_${selectedYM.value}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('PDF 다운로드 오류:', error);
    alert('일정표 PDF 다운로드에 실패했습니다.');
  }
};

const printAllBeneficiariesPdf = async () => {
  if (!memberCode.value || periodMode.value === 'all') return;
  
  const count = filteredBeneficiaryList.value.length;
  if (count === 0) {
    alert('출력할 수급자가 없습니다.');
    return;
  }

  if (!confirm(`${selectedYear.value}년 ${selectedMonth.value}월 전체 수급자(${count}명)의 일정표를 PDF로 출력하시겠습니까?`)) {
    return;
  }

  isPdfLoading.value = true;
  try {
    const res = await homeScheduleApi.downloadAllBeneficiariesPdf(memberCode.value, selectedYM.value, bizGubun.value);
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `전체수급자_일정표_${selectedYM.value}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('전체 PDF 다운로드 오류:', error);
    alert('전체 수급자 일정표 PDF 다운로드에 실패했습니다.');
  } finally {
    isPdfLoading.value = false;
  }
};

const onLtcsRequested = () => {
  setTimeout(() => {
    fetchData();
  }, 3000);
};

const openCalendarModal = (beneficiary: Beneficiary) => {
  calendarBeneficiary.value = beneficiary;
  showCalendarModal.value = true;
};

const onCalendarBeneficiaryChange = (beneficiary: Beneficiary) => {
  calendarBeneficiary.value = beneficiary;
};

watch([selectedYear, selectedMonth], () => {
  if (periodMode.value === 'month') {
    fetchMonthBeneficiaries();
  }
});

watch(memberCode, () => {
  if (memberCode.value) {
    fetchData();
  }
});

onMounted(() => {
  if (memberCode.value) {
    fetchData();
  } else {
    isLoading.value = false;
  }
});
</script>
